import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../../api/axios";

import SelectionHeader from "../../components/selection/SelectionHeader";
import AvailableList from "../../components/selection/AvailableList";
import SelectedList from "../../components/selection/SelectedList";

/**
 * Generic Team Selection Page
 *
 * Used by:
 * - Competition → Teams
 * - Series → Teams
 *
 * Props:
 * - title: string
 * - teamsEndpoint: "/api/competitions/:id/teams/" | "/api/series/:id/teams/"
 */
export default function TeamSelection({ title, teamsEndpoint }) {
  const { id } = useParams();
  const navigate = useNavigate();

  const [availableItems, setAvailableItems] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [initialSelectedItems, setInitialSelectedItems] = useState([]);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");

  useEffect(() => {
    const endpoint = teamsEndpoint.replace(":id", id);

    async function load() {
      try {
        const [assignedRes, allRes] = await Promise.all([
          api.get(endpoint),
          api.get("/api/coredata/my-teams"),
        ]);
        const assignedTeams = assignedRes.data.teams || [];

        setSelectedItems(assignedTeams);
        setInitialSelectedItems(assignedTeams);
        setAvailableItems(allRes.data || []);
      } catch (err) {
        alert(err?.response?.data?.detail || "Failed to load teams");
      } finally {
        setLoading(false);
      }
    }

    load();
  }, [id, teamsEndpoint]);

  const toggleItem = (item) => {
    setSelectedItems((prev) =>
      prev.some((t) => t.id === item.id)
        ? prev.filter((t) => t.id !== item.id)
        : [...prev, item],
    );
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.post(teamsEndpoint.replace(":id", id), {
        team_ids: selectedItems.map((t) => t.id),
      });
      navigate(-1);
    } catch (err) {
      alert(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const isDirty =
    JSON.stringify(selectedItems) !== JSON.stringify(initialSelectedItems);

  if (loading) {
    return (
      <div className="min-h-screen bg-bg-main flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-button-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-text-secondary">Loading teams...</p>
        </div>
      </div>
    );
  }

  const filteredAvailable = availableItems.filter((t) =>
    t.name.toLowerCase().includes(search.toLowerCase()),
  );

  return (
    <div className="min-h-screen bg-bg-main">
      {/* Header */}
      <div className="bg-bg-primary border-b border-bg-secondary shadow-lg sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate(-1)}
                className="p-2 rounded-lg bg-bg-secondary hover:bg-bg-main transition-colors"
              >
                <svg
                  className="w-5 h-5 text-text-secondary"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10 19l-7-7m0 0l7-7m-7 7h18"
                  />
                </svg>
              </button>
              <div>
                <h1 className="text-2xl font-bold text-text-primary">
                  {title}
                </h1>
                <p className="text-sm text-text-secondary">
                  Select teams to assign
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              {isDirty && (
                <div className="flex items-center gap-2 px-3 py-2 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                  <div className="w-2 h-2 bg-amber-500 rounded-full animate-pulse"></div>
                  <span className="text-sm text-amber-400">
                    Unsaved changes
                  </span>
                </div>
              )}

              <button
                onClick={() => navigate(-1)}
                className="px-4 py-2 bg-bg-secondary text-text-secondary rounded-lg hover:bg-bg-main transition-colors"
              >
                Cancel
              </button>

              <button
                onClick={handleSave}
                disabled={!isDirty || saving}
                className="px-6 py-2 rounded-lg font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                style={{
                  backgroundColor: isDirty ? "#6B1991" : undefined,
                  color: isDirty ? "#FFFFFF" : undefined,
                }}
              >
                {saving ? (
                  <>
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                        fill="none"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    Saving...
                  </>
                ) : (
                  <>
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    Save Changes
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto p-6">
        {/* Stats Bar */}
        <div className="grid md:grid-cols-3 gap-4 mb-6">
          <div className="bg-bg-primary rounded-lg p-4 border border-bg-secondary">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-text-secondary mb-1">
                  Total Available
                </p>
                <p className="text-2xl font-bold text-text-primary">
                  {availableItems.length}
                </p>
              </div>
              <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                <svg
                  className="w-5 h-5 text-blue-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 10h16M4 14h16M4 18h16"
                  />
                </svg>
              </div>
            </div>
          </div>

          <div className="bg-bg-primary rounded-lg p-4 border border-bg-secondary">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-text-secondary mb-1">
                  Currently Selected
                </p>
                <p className="text-2xl font-bold text-text-primary">
                  {selectedItems.length}
                </p>
              </div>
              <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
                <svg
                  className="w-5 h-5 text-purple-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
            </div>
          </div>

          <div className="bg-bg-primary rounded-lg p-4 border border-bg-secondary">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-text-secondary mb-1">Changes Made</p>
                <p className="text-2xl font-bold text-text-primary">
                  {Math.abs(selectedItems.length - initialSelectedItems.length)}
                </p>
              </div>
              <div className="w-10 h-10 bg-emerald-500/20 rounded-lg flex items-center justify-center">
                <svg
                  className="w-5 h-5 text-emerald-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4"
                  />
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* Selection Grid */}
        <div className="grid lg:grid-cols-2 gap-6">
          <AvailableList
            items={filteredAvailable}
            selectedItems={selectedItems}
            onToggle={toggleItem}
            search={search}
            onSearch={setSearch}
          />

          <SelectedList items={selectedItems} onRemove={toggleItem} />
        </div>
      </div>
    </div>
  );
}
